import { Box } from "@mui/material";
import { DotLegalButton, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import ActivityFormBox from "./ActivityFormBox";
import { ActivityContractSourceSectionProps, useActivityContractSourceSection } from "./ActivityContractSourceSection.hooks";
import { useActivityFormStyles } from "./ActivityForm.styles";

const CLAUSE_ROWS = 4;

function ActivityContractSourceSection(props: ActivityContractSourceSectionProps) {
    const { translateString } = useTranslation();
    const styles = useActivityFormStyles();
    const {
        isLoading,
        documentOptions,
        selectedOption,
        openTextSelection,
        openClausePreview,
        onDocumentChange,
        onSectionChange,
        onClauseChange,
        canOpenDocument,
        showPreviewClause,
        previewUnavailableReason,
    } = useActivityContractSourceSection(props);

    function renderActionButton(label: string, onClick: () => void) {
        return (
            <DotLegalButton buttonType="secondary" size="small" disabled={!canOpenDocument} toolTip={previewUnavailableReason} onClick={onClick}>
                {label}
            </DotLegalButton>
        );
    }

    return (
        <ActivityFormBox title={translateString("obligationContractSourceHeader")} startsWithFieldLabel>
            <Box sx={styles.singleColumn}>
                <DotLegalSelect
                    label={translateString("document")}
                    options={documentOptions}
                    isLoading={isLoading}
                    selectedItem={props.model.contractDocumentId ?? ""}
                    onChange={onDocumentChange}
                    placeholder={""}
                    noOptionsLabel={translateString("noOptions")}
                    disabled={props.readOnly}
                    useFixedLabel
                />

                {props.model.contractDocumentId && (
                    <>
                        <DotLegalTextField
                            label={translateString("documentSection")}
                            value={props.model.section ?? null}
                            debounce={false}
                            onChange={onSectionChange}
                            disabled={props.readOnly}
                            useFixedLabel
                        />

                        <DotLegalTextField
                            label={translateString("clauseText")}
                            value={props.model.clause ?? null}
                            multiline
                            rows={CLAUSE_ROWS}
                            debounce={false}
                            onChange={onClauseChange}
                            disabled={props.readOnly}
                            useFixedLabel
                        />

                        {!props.hideContractSourcePreviewActions && (
                            <Box sx={{ display: "flex", gap: 1, marginTop: 2 }}>
                                {selectedOption?.isLink ? (
                                    <DotLegalButton buttonType="secondary" href={selectedOption.link} size="small">
                                        {translateString("openLink")}
                                    </DotLegalButton>
                                ) : (
                                    // In read mode the previewer stays reachable, so the person carrying out the task can see
                                    // which clause the activity comes from. Only the way of rewriting the clause goes away.
                                    <>
                                        {!props.readOnly && renderActionButton(translateString("selectTextInDocument"), openTextSelection)}
                                        {showPreviewClause && renderActionButton(translateString("previewClause"), openClausePreview)}
                                    </>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </ActivityFormBox>
    );
}

export default ActivityContractSourceSection;
